import { FormInstance } from "antd";
import { SorterResult } from "antd/es/table/interface";
import { PERMISSION_DATA } from "api/ApiPath";
import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { PermissionDataInjection } from "features/permissionDataManagement/di/PermissionDataInjection";
import { PermissionDataActionEntity } from "features/permissionDataManagement/domain/entities/PermissionDataActionEntity";
import { PermissionDataDetailEntity } from "features/permissionDataManagement/domain/entities/PermissionDataDetailEntity";
import { PermissionDataRequestEntity } from "features/permissionDataManagement/domain/entities/PermissionDataRequestEntity";
import { PermissionDataResponseEntity } from "features/permissionDataManagement/domain/entities/PermissionDataResponseEntity";
import { RefObject } from "react";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";

class PermissionDataInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public fetchList = new BehaviorSubject<PermissionDataRequestEntity>({});
  public fetchRole = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchDetail = new BehaviorSubject<PermissionDataRequestEntity>({});
  public fetchParentMenu = new BehaviorSubject<PermissionDataRequestEntity>({});
  public fetchUsers = new BehaviorSubject<BaseUserRequestModel>({});
  public fetchCapability = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchActions = new BehaviorSubject<{
    action?: "create" | "update" | "delete";
    value?: PermissionDataActionEntity | PermissionDataRequestEntity;
    detail?: PermissionDataDetailEntity;
    currentRoleCode?: string;
  }>({});

  [Symbol.dispose](): void {
    this.loading.complete();
    this.fetchList.complete();
    this.fetchRole.complete();
    this.fetchCapability.complete();
    this.fetchDetail.complete();
    this.fetchParentMenu.complete();
    this.fetchUsers.complete();
    this.fetchActions.complete();
  }
}

class PermissionDataOutput implements Disposable {
  public savedList = new BehaviorSubject<Array<PermissionDataResponseEntity>>(
    [],
  );
  public savedCapabilities = new BehaviorSubject<
    Array<BaseFilterListResponseModel>
  >([]);
  public savedRole = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public savedDetail = new BehaviorSubject<PermissionDataDetailEntity>({});
  public savedPaging = new BehaviorSubject<Paging>({});
  public onError = new BehaviorSubject<Error>({});
  public savedParentMenu = new BehaviorSubject<
    Array<PermissionDataResponseEntity>
  >([]);
  public onActionSuccess = new BehaviorSubject<{
    action?: "create" | "update" | "delete";
    needRefreshStartup?: boolean;
  }>({});
  public savedUsers = new BehaviorSubject<Array<BaseUserResponseModel>>([]);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedCapabilities.complete();
    this.savedDetail.complete();
    this.savedRole.complete();
    this.savedUsers.complete();
    this.savedPaging.complete();
    this.onError.complete();
    this.savedParentMenu.complete();
    this.onActionSuccess.complete();
  }
}

@injectable()
export class PermissionDataViewModel extends BaseViewModel<
  PermissionDataInput,
  PermissionDataOutput
> {
  constructor() {
    super(new PermissionDataInput(), new PermissionDataOutput());
  }
  private permissionDataUseCase = PermissionDataInjection.getUseCase();
  private filterUseCase = FilterInjection.getUseCase();

  public buildTableQuery(
    pagination: any,
    sorter: SorterResult,
    filterForm: RefObject<FormInstance<any>>,
    prevParams: PermissionDataRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const order =
      sorter?.order === "descend"
        ? `${String(sorter?.column?.dataIndex)} DESC`
        : sorter?.order === "ascend"
          ? `${String(sorter?.column?.dataIndex)} ASC`
          : "createdAt DESC";

    const query = {
      ...prevParams,
      ...rest,
      created: created ?? prevParams?.created,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
      order,
    };

    const paginationState = {
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    return {
      query,
      pagination: paginationState,
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.output.savedList.next([]);
        this.input.loading.next(true);
        const res = await this.permissionDataUseCase.getList(params);
        if (res?.data) {
          this.output.savedList.next(res?.data);
          this.output.savedPaging.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.savedList.next([]);
        this.output.savedPaging.next({});
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.permissionDataUseCase.getDetail(params);
        if (res?.data) {
          let mappedDetail = new PermissionDataDetailEntity().removeUnknowValue(
            res?.data,
          );
          this.output.savedDetail.next(mappedDetail);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchRole, async (params) => {
      try {
        const res = await this.permissionDataUseCase.getRole(params);
        if (res?.data) {
          this.output.savedRole.next(res?.data);
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchParentMenu, async (params) => {
      try {
        const res = await this.permissionDataUseCase.getParentMenu(params);
        if (res?.data) {
          this.output.savedParentMenu.next(res?.data);
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchCapability, async (params) => {
      try {
        const res = await this.permissionDataUseCase.getCapability(params);
        if (res?.data) {
          this.output.savedCapabilities.next(res?.data);
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchUsers, async (params) => {
      try {
        const res = await this.filterUseCase.fetchListWithFeature({
          path: PERMISSION_DATA.USER_LIST,
          isNeedFeature: true,
          params,
        });
        if (res) {
          this.output.savedUsers.next(res);
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (payload) => {
      try {
        this.input.loading.next(true);

        if (payload?.action === "create" && payload?.value) {
          const res = await this.permissionDataUseCase.createMenu(
            payload.value as PermissionDataActionEntity,
            payload?.detail || {},
          );
          if (res?.data) {
            this.output.onActionSuccess.next({
              action: "create",
              needRefreshStartup: true,
            });
          }
        }

        if (payload?.action === "update" && payload?.value && payload?.detail) {
          const needRefreshStartup =
            this.permissionDataUseCase.shouldRefreshStartupOnUpdate(
              payload.value as PermissionDataActionEntity,
              payload.detail,
              payload.currentRoleCode,
            );
          const res = await this.permissionDataUseCase.updateMenu(
            payload.value as PermissionDataActionEntity,
            payload.detail,
          );
          if (res?.data) {
            this.output.onActionSuccess.next({
              action: "update",
              needRefreshStartup,
            });
          }
        }

        if (payload?.action === "delete" && payload?.value) {
          const res = await this.permissionDataUseCase.deleteMenu(
            payload?.value as PermissionDataRequestEntity,
          );
          if (res?.data) {
            this.output.onActionSuccess.next({
              action: "delete",
              needRefreshStartup: true,
            });
          }
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });
  }
}
